// Stage graphics for external-tournament seating (docs/plans/ext-teams-seating.md).
// Everything is drawn FROM THE CROWD'S SEAT: the crowd is at the bottom of the picture, the stage
// in front of it, so the screen's left is the crowd's left (= stage_side "left", seats 1–5).
// Pure SVG in the panel's dark tokens (client/DESIGN.md): no gradients, no glow.
import React from 'react';

// Inline token mirror (DESIGN.md "cssVars" pattern) — SVG presentation attributes can't use var().
export const S = {
  bg: '#0a0a0f', card: '#111118', elevated: '#1a1a24', input: '#0e0e15',
  line: 'rgba(255,255,255,0.06)', line2: 'rgba(255,255,255,0.09)', line3: 'rgba(255,255,255,0.14)',
  text: '#e8e8ed', sub: '#8b8b9e', muted: '#4a4a5a',
};
// Team tints: team1 / Team A = blue, team2 / Team B = orange (same as the veto page).
export const TEAM_TINT = { team1: '#60a5fa', team2: '#fb923c' };

/** Seats array helper: put `sid` in seat index `i`; if it sat elsewhere the two swap. */
export function swapSeat(seats, i, sid) {
  const next = [...seats];
  const j = next.indexOf(sid);
  if (j >= 0) next[j] = next[i];
  next[i] = sid;
  return next;
}

/**
 * One player position seen from above: desk with monitor, keyboard and mouse, the chair behind it.
 * Local frame: the player looks toward -y (monitor side), the chair is at +y. `angle` rotates it.
 */
export function SeatUnit({ x, y, angle = 0, tint = S.sub, filled = true, n, scale = 1 }) {
  const stroke = filled ? tint : S.line3;
  return (
    <g transform={`translate(${x} ${y}) rotate(${angle}) scale(${scale})`}>
      {/* chair: seat + backrest + base */}
      <circle cx="0" cy="30" r="12" fill={S.elevated} stroke={S.line3} strokeWidth="1.2" />
      <path d="M -13 40 Q 0 50 13 40" fill="none" stroke={filled ? stroke : S.line3} strokeWidth="3.2" strokeLinecap="round" opacity={filled ? 0.85 : 0.6} />
      {/* desk */}
      <rect x="-36" y="-16" width="72" height="28" rx="4" fill={S.card} stroke={S.line3} strokeWidth="1.2" />
      {/* monitor (thin, facing the player) + stand */}
      <rect x="-23" y="-14" width="46" height="5" rx="1.5" fill={S.input} stroke={stroke} strokeWidth="1.3" />
      <rect x="-3" y="-9" width="6" height="4" rx="1" fill={S.line3} />
      {/* keyboard + mouse */}
      <rect x="-15" y="-1" width="24" height="7" rx="1.5" fill={S.elevated} stroke={S.line2} strokeWidth="1" />
      <ellipse cx="16" cy="2.5" rx="2.6" ry="3.6" fill={S.elevated} stroke={S.line2} strokeWidth="1" />
      {n != null && (
        <g transform={`rotate(${-angle} 0 30)`}>
          <text x="0" y="30" dy="0.35em" textAnchor="middle" fontSize="10" fontWeight="800" fill={filled ? tint : S.muted}
            style={{ fontFamily: "var(--font-mono, 'JetBrains Mono', monospace)" }}>{n}</text>
        </g>
      )}
    </g>
  );
}

/** The crowd block: rows of heads, a label, and arrows pointing at the stage (upwards). */
export function Crowd({ x, y, w, h, label, arrows = 3 }) {
  const heads = [];
  const rows = 3, step = 22;
  const cols = Math.max(6, Math.floor((w - 40) / step));
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const cx = x + 20 + c * step + (r % 2 ? step / 2 : 0);
      if (cx > x + w - 16) continue;
      heads.push(<circle key={`${r}-${c}`} cx={cx} cy={y + 22 + r * 15} r="5" fill={S.elevated} stroke={S.line2} strokeWidth="1" />);
    }
  }
  const arrowXs = Array.from({ length: arrows }, (_, i) => x + (w * (i + 1)) / (arrows + 1));
  return (
    <g>
      {arrowXs.map((ax, i) => (
        <g key={i} stroke={S.line3} strokeWidth="1.4" fill="none" strokeLinecap="round">
          <line x1={ax} y1={y - 8} x2={ax} y2={y - 46} strokeDasharray="3 5" />
          <path d={`M ${ax - 5} ${y - 40} L ${ax} ${y - 47} L ${ax + 5} ${y - 40}`} />
        </g>
      ))}
      <rect x={x} y={y} width={w} height={h} rx="12" fill={S.card} stroke={S.line2} strokeWidth="1.2" />
      {heads}
      <text x={x + w / 2} y={y + h - 12} textAnchor="middle" fontSize="11" fontWeight="700" letterSpacing="0.08em" fill={S.sub}
        style={{ textTransform: 'uppercase', fontFamily: "var(--font-sans, 'Satoshi', sans-serif)" }}>{label}</text>
    </g>
  );
}

/** Seat card (the "display board" behind the player): seat number + a player dropdown. HTML, placed over the SVG. */
export function SeatCard({ n, tint, value, options, onChange, readOnly, emptyLabel, style }) {
  const cur = options.find(o => o.value === value);
  return (
    <div style={{
      width: 150, background: S.card, border: `1px solid ${value ? `${tint}55` : S.line3}`, borderRadius: 8,
      padding: '0.35rem 0.45rem 0.45rem', boxShadow: '0 2px 8px rgba(0,0,0,0.25)', boxSizing: 'border-box', ...style,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <span style={{ fontFamily: "var(--font-mono, monospace)", fontSize: '0.66rem', fontWeight: 800, color: tint, background: `${tint}14`, border: `1px solid ${tint}30`, borderRadius: 4, padding: '0 5px' }}>{n}</span>
        <span style={{ fontSize: '0.66rem', color: S.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cur && cur.hint}</span>
      </div>
      {readOnly ? (
        <div style={{ fontWeight: 800, fontSize: '0.86rem', color: cur ? S.text : S.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cur ? cur.label : emptyLabel}</div>
      ) : (
        <select value={value || ''} onChange={e => onChange(e.target.value)}
          style={{ width: '100%', padding: '0.3rem 0.35rem', background: S.input, color: S.text, border: `1px solid ${S.line3}`, borderRadius: 6, fontSize: '0.82rem', fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer' }}>
          {!value && <option value="">{emptyLabel}</option>}
          {options.map(o => <option key={o.value} value={o.value}>{o.label}{o.sub ? ` · ${o.sub}` : ''}</option>)}
        </select>
      )}
    </div>
  );
}

/** Substitute list box beside a team. */
export function SubsBox({ title, tint, players, emptyLabel, style }) {
  return (
    <div style={{ width: 170, background: S.card, border: `1px solid ${S.line2}`, borderRadius: 12, padding: '0.7rem 0.8rem', boxSizing: 'border-box', ...style }}>
      <div style={{ fontSize: '0.66rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: S.muted, marginBottom: '0.45rem' }}>{title}</div>
      {players.length === 0 && <div style={{ fontSize: '0.78rem', color: S.muted }}>{emptyLabel}</div>}
      <div style={{ display: 'grid', gap: 5 }}>
        {players.map(p => (
          <div key={p.steam_id} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: '0.84rem', fontWeight: 700, color: S.text }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: tint, opacity: 0.6, flexShrink: 0 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.ign}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
